import { Component, computed, input, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ModalService } from "../../modules/modal/modal.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { EventSummary, EventSummaryService } from "../../modules/s25-swarm-schedule/s25.event.summary.service";
import { Item } from "../../pojo/Item";
import { EventProfile } from "../../services/event.service";
import { EventStateConst } from "../../services/event/event.state.const";
import { PreferenceService } from "../../services/preference.service";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25FavoriteButtonComponent } from "../s25-favorite-button/s25.favorite.button.component";
import { S25ItemMoreComponent } from "./s25.item.more.component";
import { Event } from "../../pojo/Event";
import RsrvStates = Event.Reservation.States;

type ProfileWithSummaries = EventSummary.Profile & {
    summaries: string[][];
};

@TypeManagerDecorator("s25-event-summary-data")
@Component({
    selector: "s25-ng-event-summary-data",
    standalone: true,
    host: {
        "[class.ng-event-summary-data]": "true",
        "[class.isModal]": "isModal()",
    },
    styleUrls: ["./s25.event.summary.data.component.scss"],
    template: `
        @if (summary()) {
            <div class="c-eventSummaryTitle">
                <s25-ng-icon
                    class="event-icon"
                    type="event"
                    [ariaLabel]="false"
                    [autoDarkMode]="true"
                    svgClass="c-svgIcon"
                />
                <h1 class="ngEventSummaryName">
                    {{ summary().event_name }}
                </h1>
                @if (!isModal()) {
                    <s25-ng-favorite-button class="event-favorite" [itemId]="eventId()" [itemTypeId]="Item.Ids.Event" />
                }
                @if (summary().event_title) {
                    <div class="ngEventSummaryTitle">
                        {{ summary().event_title }}
                    </div>
                }
            </div>
            @if (stateText()) {
                <div class="event-datum ngEventSummaryState">
                    {{ stateText() }}
                </div>
            }
            <div class="event-datum ngEventSummaryReference">
                {{ summary().reference }}
            </div>
            @if (profiles()?.length) {
                @if (!isModal()) {
                    @let firstProfile = profiles()![0];
                    <div class="event-datum ngEventSummaryProfDesc">
                        @for (text of firstProfile.summaries[0]; track text) {
                            <div>{{ text }}</div>
                        }
                        @if (profiles()!.length > 1 || firstProfile.summaries.length > 1) {
                            <a class="ngBlock" (click)="openModal()">
                                @if (profiles()!.length > 1) {
                                    All Segments
                                } @else if (firstProfile.summaries.length > 1) {
                                    All Descriptions
                                }
                            </a>
                        }
                    </div>
                } @else {
                    <ul class="event-datum ngEventSummaryPattern">
                        @for (prof of profiles(); track prof.profile_id) {
                            <li class="profile">
                                @if (prof.names) {
                                    <div class="ngBold">{{ prof.names }}:</div>
                                }
                                @if (prof.summaries.length === 0) {
                                    <span class="profile-summary"> No Occurrences </span>
                                } @else if (prof.summaries.length === 1) {
                                    @for (text of prof.summaries[0]; track text) {
                                        <div class="profile-summary">
                                            {{ text }}
                                        </div>
                                    }
                                } @else if (prof.summaries.length > 1) {
                                    <ul class="profile-summary">
                                        @for (summary of prof.summaries; track summary) {
                                            <li>
                                                @for (text of summary; track text) {
                                                    <div>{{ text }}</div>
                                                }
                                            </li>
                                        }
                                    </ul>
                                }
                            </li>
                        }
                    </ul>
                }
            } @else if (hasNoReservations()) {
                <div class="event-datum ngEventSummaryNoOccurrences">No Occurrences Found</div>
            }
            @if (summary().res?.length || summary().loc?.length) {
                <div
                    class="event-datum logoContainer"
                    [class.noResources]="!summary().res?.length"
                    [class.noLocations]="!summary().loc?.length"
                >
                    @if (summary().loc?.length) {
                        <s25-ng-item-more class="ngEventSummaryLocs" [items]="summary().loc" />
                    }
                    @if (summary().res?.length) {
                        <s25-ng-item-more class="ngEventSummaryRes" [items]="summary().res" />
                    }
                </div>
            }
            <span class="refresh-placeholder" aria-hidden="true"></span>
        } @else {
            <div>(Private)</div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25ItemMoreComponent, S25FavoriteButtonComponent, S25IconComponent],
})
export class S25EventSummaryDataComponent {
    public readonly summary = input.required<EventSummary>();
    public readonly eventId = input.required<number>();
    public readonly dateFormat = input.required<string>();
    public readonly timeFormat = input.required<string>();
    public readonly isModal = input<boolean>(false);
    public readonly profileReservations = input<Array<EventProfile>>();

    public readonly displayAdditionalTime = s25Resource({
        loader: async () => {
            const addlTimePref = await PreferenceService.getPreferences(["DisplayAdditionalTime"]);
            return S25Util.toBool(addlTimePref?.DisplayAdditionalTime.value);
        },
    });

    public readonly profiles = computed<ProfileWithSummaries[] | undefined>(() => {
        const reservations = this.profileReservations();
        if (!reservations || reservations.length === 0) {
            return undefined;
        }

        return reservations
            .slice()
            .sort(S25Util.shallowSort("init_start_dt"))
            .filter((profile) => profile.reservation?.length > 0) // Only include profiles with reservations in summary
            .map((profile) => {
                let summaries: string[][];
                const reservationArray = profile.reservation || [];
                const activeReservations = reservationArray.filter(
                    (reservation) => reservation.reservation_state !== RsrvStates.Cancelled,
                );
                //ANG-6533 when several occurrences are cancelled it makes sense to print the summary as if it is ad hoc
                const useAdHocSummary =
                    !profile.profile_code ||
                    (activeReservations.length !== reservationArray.length && activeReservations.length < 6); //6 is fairly arbitrary

                if (useAdHocSummary) {
                    //No repeating pattern, but multiple reservations needs special handling
                    summaries = EventSummaryService.adhocSummaryModel(reservationArray).map(
                        (timeSet: EventSummary.TimeSet) => {
                            return EventSummaryService.formSummaryDescModel({
                                profileCode: EventSummaryService.adhocSummaryModelToProfileCode(timeSet),
                                start: S25Util.date.dropTZString(timeSet.initStartDate),
                                end: S25Util.date.dropTZString(timeSet.initEndDate),
                                dateFormat: this.dateFormat(),
                                timeFormat: this.timeFormat(),
                                timeSet: timeSet.length > 1 ? timeSet : undefined, //if there is only one reservation, don't show the time set
                                displayAdditionalTime: this.displayAdditionalTime.value(),
                                addtlStart: S25Util.date.dropTZString(timeSet.setupStartDt),
                                addtlEnd: S25Util.date.dropTZString(timeSet.takedownEndDt),
                            });
                        },
                    );
                } else {
                    summaries = [
                        EventSummaryService.formSummaryDescModel({
                            profileCode: profile.profile_code,
                            start: S25Util.date.dropTZString(profile.init_start_dt),
                            end: S25Util.date.dropTZString(profile.init_end_dt),
                            dateFormat: this.dateFormat(),
                            timeFormat: this.timeFormat(),
                            displayAdditionalTime: this.displayAdditionalTime.value(),
                            addtlStart: S25Util.date.dropTZString(reservationArray[0]!.reservation_start_dt),
                            addtlEnd: S25Util.date.dropTZString(reservationArray[0]!.reservation_end_dt),
                        }),
                    ];
                }

                return {
                    names: profile.profile_name || "",
                    rec_type: profile.rec_type_id,
                    init_start_dt: profile.init_start_dt,
                    init_end_dt: profile.init_end_dt,
                    profile_id: profile.profile_id,
                    profile_code: profile.profile_code,
                    summaries,
                };
            });
    });

    public readonly hasNoReservations = computed(() => {
        const reservations = this.profileReservations();
        const profilesList = this.profiles();

        // No profiles at all (data loaded but empty array)
        if (reservations !== undefined && reservations.length === 0) {
            return true;
        }

        // Profiles exist but all have empty summaries (no reservations on any profile)
        if (
            profilesList !== undefined &&
            profilesList.length > 0 &&
            profilesList.every((profile) => profile.summaries.length === 0)
        ) {
            return true;
        }

        return false;
    });

    public readonly stateText = computed(() => EventStateConst.stateMap[S25Util.parseInt(this.summary().state)]);

    protected readonly Item = Item;

    openModal() {
        ModalService.modal("event-summary", {
            summary: this.summary(),
            eventId: this.eventId(),
            dateFormat: this.dateFormat(),
            timeFormat: this.timeFormat(),
            profileReservations: this.profileReservations(),
        });
    }
}
